import { useRef, useState } from 'react'
import { getTraceId, isApiError, isNetworkError } from '@/shared/api'
import { ENV } from '@/shared/config'
import { useTranslation } from '@/shared/i18n'
import { createTraceId, sleep } from '@/shared/lib'
import { haptic, openUrl } from '@/shared/telegram'
import { StateView } from '@/widgets/state-view'

type AppErrorPageProps = {
  error: unknown
  /** Повтор запроса или перерисовка экрана — не перезагрузка WebView. */
  onRetry: () => void | Promise<void>
}

/** Кнопка уходит в загрузку минимум на 2 секунды, дальше задержка растёт вдвое. */
const MIN_RETRY_DELAY_MS = 2000
const MAX_RETRY_DELAY_MS = 16_000

/**
 * Ошибка приложения: error boundary, 5xx и отказ подписи Telegram (`TG-001`,
 * `TG-002`, `TG-003`, `TG-005`). У ответа бэкенда в строке кода виден и его
 * код ошибки: по скриншоту сразу понятно, что сломалось.
 *
 * Сетевой сбой (запрос не дошёл до бэкенда) — не «что-то сломалось», а экран
 * 32 «Нет соединения»: нейтральный круг, `wifiOff` цветом `tg-hint`,
 * «Повторить». trace-id там не показываем: до бэкенда запрос не дошёл, искать
 * в логах нечего.
 *
 * Последний рубеж, а не ответ по умолчанию. Во время оплаты не показывается
 * никогда — там экран «Проверяем оплату» с поллингом.
 *
 * Экрана ошибки приложения в макете нет — собран на общем шаблоне отказов:
 * нейтральный круг, иконка alert цветом `tg-destructive` (наш сбой).
 */
export const AppErrorPage = ({ error, onRetry }: AppErrorPageProps) => {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)
  const attempts = useRef(0)
  const isOffline = isNetworkError(error)
  // Для ошибки рендера trace-id свой: пишем его в консоль рядом с ошибкой,
  // чтобы скриншот экрана сводился с логом.
  const [traceId] = useState(() => {
    const id = getTraceId(error) ?? createTraceId()

    console.error(`[app-error] trace-id ${id}`, error)

    return id
  })

  const retry = async () => {
    if (isRetrying) {
      return
    }

    haptic.impact('light')
    setIsRetrying(true)
    attempts.current += 1

    try {
      await sleep(Math.min(MIN_RETRY_DELAY_MS * 2 ** (attempts.current - 1), MAX_RETRY_DELAY_MS))
      await onRetry()
    } finally {
      setIsRetrying(false)
    }
  }

  if (isOffline) {
    return (
      <StateView
        icon="wifiOff"
        title={t('netError.title')}
        text={t('netError.text')}
        haptic="error"
        mainButton={{ text: t('common.retry'), onClick: retry, isLoading: isRetrying }}
      />
    )
  }

  return (
    <StateView
      icon="alert"
      iconTone="destructive"
      title={t('appError.title')}
      text={t('appError.text')}
      code={isApiError(error) ? `${error.code} · trace-id · ${traceId}` : `trace-id · ${traceId}`}
      haptic="error"
      mainButton={{ text: t('appError.reload'), onClick: retry, isLoading: isRetrying }}
      secondary={ENV.SUPPORT_URL ? { label: t('common.support'), onClick: () => openUrl(ENV.SUPPORT_URL) } : null}
    />
  )
}
